// Privacy policy v1.0 (effective 1 September 2026), served until v1.1 takes effect on 1 November 2026
// (src/content/legal/index.ts getGdprDoc). Delete this folder after that date.
import type { LegalDoc } from "../types";

// Italiano (GDPR). Traduzione della policy di riferimento (v1.0, 1° settembre 2026).
const gdprIt: LegalDoc = {
  title: "Informativa sulla privacy e sulla protezione dei dati (GDPR / RGPD)",
  effectiveDate: "1 settembre 2026",
  version: "1.0",
  intro:
    "Questa informativa spiega come Photohawk tratta i dati personali ai sensi del Regolamento generale sulla protezione dei dati dell'UE (GDPR) e del suo equivalente francese, il RGPD (Règlement Général sur la Protection des Données), nonché del UK GDPR. RGPD e GDPR indicano la medesima normativa.",
  sections: [
    {
      heading: "1. Chi siamo",
      blocks: [
        {
          type: "p",
          text: "Photohawk è una piattaforma di fotografia di eventi che consente a fotografi e organizzatori di pubblicare, cercare e vendere foto di eventi.",
        },
        {
          type: "ul",
          items: [
            "Entità giuridica: CLOUDCONCUR LTD (numero di società 13399913), società registrata in Inghilterra e Galles, operante con il nome commerciale Photohawk.",
            "Sede legale: 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Regno Unito.",
            "Responsabile della protezione dei dati (DPO): Andrew Hammond — contatto support@photohawk.com.",
            "Rappresentante nell'UE (Art. 27 del GDPR): non abbiamo designato un rappresentante nell'UE, in quanto il nostro trattamento dei dati di residenti dell'UE è occasionale e non costituisce la nostra attività principale (Art. 27(2)). Riesaminiamo periodicamente questo aspetto e designeremo un rappresentante qualora il nostro trattamento dovesse cambiare.",
            "Autorità di controllo: l'Information Commissioner's Office (ICO) per il Regno Unito, e le autorità dell'UE, tra cui la CNIL francese, per le persone situate nell'UE.",
          ],
        },
      ],
    },
    {
      heading: "2. I nostri due ruoli — titolare e responsabile del trattamento",
      blocks: [
        {
          type: "p",
          text: "Photohawk si rivolge a due categorie di persone, e il nostro ruolo giuridico differisce per ciascuna:",
        },
        {
          type: "ul",
          items: [
            "Fotografi e organizzatori (i nostri clienti). Per i dati del vostro account (nome, e-mail, dati aziendali, credenziali di accesso, informazioni di fatturazione e di pagamento) Photohawk è il titolare del trattamento.",
            "Partecipanti agli eventi e acquirenti (i vostri clienti). Per i dati delle persone ritratte nelle foto e che le acquistano, siete voi (il fotografo/l'organizzatore) il titolare del trattamento e Photohawk è il vostro responsabile del trattamento — trattiamo tali dati secondo le vostre istruzioni per gestire le gallerie, la ricerca e le vendite che avete configurato. Un accordo sul trattamento dei dati (vedi §11) disciplina questo rapporto.",
          ],
        },
      ],
    },
    {
      heading: "3. Quali dati personali trattiamo",
      blocks: [
        { type: "p", text: "Per i fotografi/organizzatori (siamo titolari del trattamento):" },
        {
          type: "ul",
          items: [
            "Identità e contatto: nome, e-mail, telefono, ragione sociale, indirizzo.",
            "Account e sicurezza: credenziali di accesso (tramite AWS), sessioni.",
            "Dati finanziari: dati di abbonamento/fatturazione (tramite Stripe) e coordinate per i pagamenti (tramite Trolley).",
          ],
        },
        { type: "p", text: "Per i partecipanti/acquirenti (siamo responsabili del trattamento, per conto dell'organizzatore):" },
        {
          type: "ul",
          items: [
            "Identità e contatto: nome, e-mail, numero di telefono, indirizzo postale/di fatturazione.",
            "Fotografie dei partecipanti.",
            "Dati biometrici — quando è attivata la ricerca per volto/selfie (vedi §4).",
            "Dati di ordine e di pagamento (trattati tramite i nostri fornitori di servizi di pagamento; non conserviamo i numeri completi delle carte).",
            "Stato del consenso marketing, quando il partecipante presta il consenso.",
          ],
        },
      ],
    },
    {
      heading: "4. Ricerca per volto e selfie (dati biometrici di categoria particolare)",
      blocks: [
        {
          type: "p",
          text: "Quando un organizzatore attiva la ricerca per selfie/volto, Photohawk crea una rappresentazione matematica dei volti affinché un partecipante possa ritrovare le proprie foto.",
        },
        {
          type: "ul",
          items: [
            "Finalità: esclusivamente per associare una persona alle proprie foto dell'evento.",
            "Luogo: trattati e conservati nell'Unione europea (AWS, regione Irlanda).",
            "Conservazione: i dati biometrici/di volto vengono automaticamente cancellati alla scadenza di una galleria (entro ~60 giorni) e in caso di eliminazione di una galleria — a seconda di quale evento si verifichi per primo.",
            "Base giuridica (Art. 9 del GDPR): i dati biometrici costituiscono una categoria particolare e ricevono una protezione aggiuntiva: sono utilizzati solo per permettere alle persone di trovare le proprie foto in quella galleria. In qualità di titolare del trattamento per i partecipanti, l'amministratore della galleria decide se la ricerca per volto è appropriata per il suo evento e consentita nel luogo in cui si svolge. Quando la legge locale richiede il consenso delle persone fotografate, l'amministratore deve averlo, ad esempio tramite l'iscrizione all'evento; in caso contrario, deve utilizzare invece la ricerca per pettorale e per orario, che non usa dati facciali. Photohawk prevede una fase di consenso integrata nel prodotto: al partecipante viene chiesto di prestare il consenso prima che il suo selfie sia utilizzato per la ricerca per volto, e può rifiutare.",
            "Minori: vedi §13.",
          ],
        },
      ],
    },
    {
      heading: "5. Perché utilizziamo i dati e le nostre basi giuridiche",
      blocks: [
        {
          type: "ul",
          items: [
            "Per fornire il servizio (ospitare le gallerie, eseguire la ricerca, elaborare gli ordini): esecuzione di un contratto.",
            "Pagamenti, versamenti, adempimenti fiscali e contabili: esecuzione di un contratto e rispetto di un obbligo legale.",
            "Ricerca per volto/selfie (Art. 9): il consenso esplicito della persona che effettua la ricerca, prima che il suo selfie sia utilizzato; per le persone fotografate, come descritto al §4.",
            "E-mail/messaggi di marketing: solo con il consenso del destinatario (opt-in), che può essere revocato in qualsiasi momento.",
            "Cookie di analisi e pubblicitari: solo con il vostro consenso, prestato tramite il nostro banner cookie (vedi §12).",
            "Sicurezza, prevenzione delle frodi e miglioramento del servizio: il nostro legittimo interesse, bilanciato con i vostri diritti.",
          ],
        },
      ],
    },
    {
      heading: "6. Dove sono conservati i vostri dati e trasferimenti internazionali",
      blocks: [
        {
          type: "ul",
          items: [
            "Ubicazione dei dati: i dati dei partecipanti e degli account — comprese le foto e i dati biometrici del volto — sono ospitati nell'Unione europea (AWS, Irlanda / eu-west-1). Anche il nostro servizio di identità (AWS) si trova nell'UE.",
            "Trasferimenti internazionali: alcuni dei nostri sub-responsabili (vedi §7) operano al di fuori dell'UE/del Regno Unito (ad es. negli Stati Uniti). Quando i dati personali sono trasferiti al di fuori dell'UE/del Regno Unito, ci basiamo su una decisione di adeguatezza ove applicabile e, in mancanza, sulle Clausole Contrattuali Tipo (SCC) della Commissione europea unitamente all'Addendum sul trasferimento internazionale di dati del Regno Unito, con ulteriori garanzie ove necessario.",
          ],
        },
      ],
    },
    {
      heading: "7. Sub-responsabili del trattamento",
      blocks: [
        {
          type: "p",
          text: "Ci avvaliamo di un numero ristretto di fornitori accuratamente selezionati per erogare il servizio, ciascuno vincolato da un accordo sul trattamento dei dati (e dalle SCC/Addendum UK quando trattano dati al di fuori dell'UE/del Regno Unito):",
        },
        {
          type: "table",
          head: ["Fornitore", "Finalità", "Dati coinvolti"],
          rows: [
            ["Amazon Web Services (AWS)", "Hosting, archiviazione, identità, ricerca per volto, e-mail transazionali — regione UE", "Tutti i dati personali, foto, dati biometrici"],
            ["Cloudflare (incl. R2)", "CDN, domini personalizzati, archiviazione di oggetti", "Foto, dati delle richieste"],
            ["Stripe", "Pagamenti con carta e abbonamenti", "Dati di pagamento di acquirenti e clienti"],
            ["Xendit", "Pagamenti (Sud-est asiatico)", "Dati di pagamento degli acquirenti"],
            ["Paystack", "Pagamenti (Africa)", "Dati di pagamento degli acquirenti"],
            ["Trolley", "Pagamenti ai fotografi", "Identità e coordinate bancarie del beneficiario"],
            ["Postmark", "E-mail transazionali", "Nomi, indirizzi e-mail"],
            ["Twilio / WhatsApp", "Consegna delle foto e notifiche", "Nomi, numeri di telefono"],
            ["Google (Google Analytics)", "Analisi dell'utilizzo del sito web (soggetta a consenso)", "Dati di utilizzo/dispositivo, identificatori"],
            ["Meta Platforms (Meta Pixel)", "Pubblicità e misurazione delle conversioni (soggetta a consenso)", "Dati di utilizzo, identificatori"],
          ],
        },
        { type: "p", text: "Un elenco aggiornato è disponibile su richiesta." },
      ],
    },
    {
      heading: "8. Per quanto tempo conserviamo i dati (conservazione)",
      blocks: [
        {
          type: "ul",
          items: [
            "Dati biometrici / di volto: cancellati entro ~60 giorni dalla scadenza di una galleria e in caso di eliminazione di una galleria (vedi §4).",
            "Foto e video: la conservazione è controllata dal titolare dell'account (l'organizzatore/il fotografo). I contenuti sono conservati finché il titolare dell'account li mantiene; i titolari dell'account possono eliminare foto, video, gallerie e schede dei partecipanti in qualsiasi momento, e noi le eliminiamo di conseguenza. Photohawk non impone una durata di vita fissa a questi contenuti.",
            "Ordini e documenti finanziari: conservati per 7 anni per adempiere agli obblighi fiscali e contabili.",
            "Schede di contatto dei partecipanti/acquirenti: controllate dall'organizzatore — conservate finché l'organizzatore le mantiene, ed eliminate quando l'organizzatore le elimina o su richiesta di cancellazione verificata.",
            "Liste di marketing: conservate fino alla revoca del consenso.",
            "I log operativi/di sicurezza sono conservati per 30 giorni.",
          ],
        },
      ],
    },
    {
      heading: "9. Come proteggiamo i dati",
      blocks: [
        {
          type: "ul",
          items: [
            "In transito: cifrati tramite HTTPS/TLS ovunque.",
            "A riposo: le foto e i contenuti sono conservati su AWS e Cloudflare R2, che cifrano i dati a riposo per impostazione predefinita; le credenziali sensibili (ad es. i token degli account collegati) sono inoltre cifrate con AWS.",
            "Controlli di accesso: accesso autenticato tramite AWS; l'accesso del personale è limitato a quanto necessario per gestire il servizio.",
          ],
        },
      ],
    },
    {
      heading: "10. I vostri diritti",
      blocks: [
        {
          type: "p",
          text: "Ai sensi del GDPR/RGPD e del UK GDPR avete il diritto di: accedere ai vostri dati; rettificarli; cancellarli (“diritto all'oblio”); limitare il trattamento od opporvi ad esso; la portabilità dei dati; e revocare il consenso in qualsiasi momento. Avete inoltre il diritto di presentare un reclamo a un'autorità di controllo (l'ICO nel Regno Unito, o la vostra autorità locale come la CNIL in Francia).",
        },
        { type: "p", text: "Come esercitarli:" },
        {
          type: "ul",
          items: [
            "Fotografi/organizzatori (siamo titolari del trattamento): contattate il nostro DPO all'indirizzo support@photohawk.com. Rispondiamo entro un mese.",
            "Partecipanti/acquirenti (l'organizzatore è titolare del trattamento): vi invitiamo a contattare il fotografo o l'organizzatore che ha gestito il vostro evento. I fotografi possono eliminare i dati personali di un partecipante — comprese le sue foto ed eventuali dati di ricerca per volto — direttamente da Photohawk, e Photohawk darà seguito a una richiesta verificata per conto dell'organizzatore.",
          ],
        },
        {
          type: "p",
          text: "La chiusura di un account Photohawk pone fine alla fatturazione ma non comporta di per sé la cancellazione dei dati conservati; chiedeteci la cancellazione dei dati se è ciò di cui avete bisogno.",
        },
      ],
    },
    {
      heading: "11. Accordo sul trattamento dei dati (DPA)",
      blocks: [
        {
          type: "p",
          text: "Per i fotografi/organizzatori, il nostro trattamento dei dati dei vostri partecipanti è disciplinato da un accordo sul trattamento dei dati ai sensi dell'art. 28 del GDPR, che mettiamo a disposizione su richiesta all'indirizzo support@photohawk.com.",
        },
      ],
    },
    {
      heading: "12. Cookie e tecnologie simili",
      blocks: [
        {
          type: "ul",
          items: [
            "I cookie strettamente necessari (per l'accesso, la sicurezza e le funzionalità di base) sono sempre attivi; non richiedono consenso.",
            "I cookie di analisi e pubblicitari — Google Analytics e il Meta Pixel (vedi §7) — non sono essenziali e vengono caricati solo dopo che avete prestato il consenso tramite il nostro banner di consenso ai cookie. Potete modificare o revocare la vostra scelta in qualsiasi momento tramite il banner / le impostazioni dei cookie. Il rifiuto lascia il servizio pienamente utilizzabile.",
            "Maggiori dettagli sono riportati nella nostra separata Informativa sui cookie.",
          ],
        },
      ],
    },
    {
      heading: "13. Dati dei minori",
      blocks: [
        {
          type: "p",
          text: "Gli eventi possono includere minori. Decidere se la ricerca per volto è appropriata per un evento spetta all'amministratore della galleria (vedi §4); qualora possano essere presenti minori e la legge locale richieda il consenso delle persone fotografate, l'amministratore deve avere per loro il consenso di un genitore o tutore, oppure utilizzare invece la ricerca per pettorale e per orario. Photohawk non crea consapevolmente dati biometrici relativi a un minore senza un consenso richiesto dalla legge, e la fase di consenso integrata nel prodotto rafforza questo principio.",
        },
      ],
    },
    {
      heading: "14. Violazioni dei dati",
      blocks: [
        {
          type: "p",
          text: "In caso di violazione dei dati personali, Photohawk notificherà il titolare del trattamento interessato senza ingiustificato ritardo dopo esserne venuta a conoscenza, e lo assisterà nell'adempimento dei propri obblighi di notifica alle autorità di controllo e alle persone interessate (Arts. 33–34 del GDPR).",
        },
      ],
    },
    {
      heading: "15. Modifiche a questa informativa",
      blocks: [
        {
          type: "p",
          text: "Potremmo aggiornare questa informativa; la “Data di efficacia” sopra indicata mostra la versione in vigore. Le modifiche sostanziali saranno comunicate ai titolari dell'account.",
        },
      ],
    },
    {
      heading: "16. Contatti",
      blocks: [
        {
          type: "p",
          text: "Responsabile della protezione dei dati — Andrew Hammond. CLOUDCONCUR LTD (numero di società 13399913), operante con il nome Photohawk. E-mail: support@photohawk.com · 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Regno Unito.",
        },
      ],
    },
  ],
};

export default gdprIt;
